import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Send, Plus, Trash2, Edit3, X, Link as LinkIcon, Music, Megaphone, BookOpen, HeartHandshake, MessageCircle, Check, Clock, Users, Calendar, BarChart3, MapPin, Download } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

const CATS = [
  { key: "announcement", label: "Announcement", icon: Megaphone },
  { key: "event", label: "Event", icon: Calendar },
  { key: "poll", label: "Poll", icon: BarChart3 },
  { key: "songlist", label: "Song List", icon: Music },
  { key: "music", label: "Music Link", icon: Music },
  { key: "prayer", label: "Prayer", icon: HeartHandshake },
  { key: "general", label: "General", icon: MessageCircle },
];

export default function PortalGroupDetail() {
  const { slug } = useParams();
  const [groupId, setGroupId] = useState(null);
  const [data, setData] = useState({ group: null, posts: [], my_role: null });
  const [loading, setLoading] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("feed");

  const loadByGroupId = useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/portal/groups/${id}`);
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load group");
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data: groups } = await api.get("/portal/groups");
        const found = groups.find((g) => g.slug === slug);
        if (!found) {
          toast.error("Group not found");
          setLoading(false);
          return;
        }
        setGroupId(found.id);
        await loadByGroupId(found.id);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, loadByGroupId]);

  const isLeader = data.my_role === "leader" || data.my_role === "admin";

  if (loading) {
    return <div className="py-20 flex justify-center"><div className="w-8 h-8 border-2 border-electric border-t-transparent rounded-full animate-spin" role="status" /></div>;
  }
  if (!data.group) {
    return (
      <div className="text-center py-20">
        <p className="text-zinc-400 mb-4">Group not found or you don't have access.</p>
        <Link to="/portal/groups" className="text-electric font-bold">Back to Groups</Link>
      </div>
    );
  }

  const g = data.group;

  return (
    <div data-testid="portal-group-detail">
      <Link to="/portal/groups" className="inline-flex items-center gap-2 text-zinc-400 hover:text-electric text-sm font-bold uppercase tracking-wider mb-6" data-testid="group-back">
        <ArrowLeft className="w-4 h-4" /> All Groups
      </Link>

      {/* HEADER */}
      <div className="mb-10 flex items-start gap-5 flex-wrap">
        <div className="text-6xl" aria-hidden="true">{g.icon || "✨"}</div>
        <div className="flex-1 min-w-0">
          <h1 className="font-display font-black text-3xl lg:text-5xl tracking-tighter mb-2">{g.name}</h1>
          <p className="text-zinc-400 leading-relaxed">{g.description}</p>
          {isLeader && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-electric/10 border border-electric/20">
              <span className="text-[10px] uppercase tracking-wider text-electric font-bold">You're a leader</span>
            </div>
          )}
        </div>
        {isLeader && (
          <button
            onClick={() => { setEditing(null); setShowCompose(true); }}
            className="inline-flex items-center gap-2 bg-electric text-black px-5 py-2.5 rounded-full font-bold text-sm hover:bg-[#4ade80] transition"
            data-testid="group-new-post"
          >
            <Plus className="w-4 h-4" /> New Post
          </button>
        )}
      </div>

      {/* TABS (for leaders) */}
      {isLeader && (
        <div className="flex gap-2 mb-6 border-b border-white/5">
          <button onClick={() => setTab("feed")} className={`px-5 py-3 text-sm font-bold uppercase tracking-wider transition relative ${tab === "feed" ? "text-electric" : "text-zinc-400 hover:text-white"}`} data-testid="tab-feed">
            Feed
            {tab === "feed" && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-electric" />}
          </button>
          <button onClick={() => setTab("members")} className={`px-5 py-3 text-sm font-bold uppercase tracking-wider transition relative ${tab === "members" ? "text-electric" : "text-zinc-400 hover:text-white"}`} data-testid="tab-members">
            Members
            {tab === "members" && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-electric" />}
          </button>
        </div>
      )}

      {/* COMPOSE */}
      <AnimatePresence>
        {showCompose && (
          <PostComposer
            groupId={groupId}
            initial={editing}
            onClose={() => { setShowCompose(false); setEditing(null); }}
            onSaved={() => { setShowCompose(false); setEditing(null); loadByGroupId(groupId); }}
          />
        )}
      </AnimatePresence>

      {tab === "feed" ? (
        <div>
          {data.posts.length === 0 ? (
            <div className="p-12 rounded-2xl border border-white/5 bg-[#0a0a0a] text-center">
              <MessageCircle className="w-10 h-10 text-electric mx-auto mb-4" aria-hidden="true" />
              <h3 className="font-display font-bold text-xl mb-2">No posts yet</h3>
              <p className="text-zinc-400 text-sm">{isLeader ? "Share the first announcement, song list, or update." : "Your leaders will post here. Check back soon."}</p>
            </div>
          ) : (
            <div className="space-y-5">
              {data.posts.map((p) => {
                const cat = CATS.find((c) => c.key === p.category) || CATS[0];
                const CatIcon = cat.icon;
                return (
                  <motion.article
                    key={p.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-7 rounded-2xl border border-white/5 bg-[#0a0a0a]"
                    data-testid={`post-${p.id}`}
                  >
                    <div className="flex items-center gap-3 text-xs uppercase tracking-wider mb-4">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-electric/10 text-electric border border-electric/20 font-bold">
                        <CatIcon className="w-3 h-3" aria-hidden="true" /> {cat.label}
                      </span>
                      <span className="text-zinc-500">{p.author_name}</span>
                      <span className="text-zinc-600 ml-auto">{fmt(p.created_at)}</span>
                    </div>
                    <h3 className="font-display font-bold text-2xl tracking-tight mb-3">{p.title}</h3>
                    <div className="prose prose-invert max-w-none text-zinc-200 prose-p:my-3 prose-a:text-electric prose-headings:font-display prose-headings:text-white">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{p.body}</ReactMarkdown>
                    </div>
                    {p.event?.event_date && <EventBlock post={p} />}
                    {p.poll?.question && <PollBlock post={p} groupId={groupId} onVoted={() => loadByGroupId(groupId)} />}
                    {p.links?.length > 0 && (
                      <div className="mt-5 pt-5 border-t border-white/5 space-y-2">
                        {p.links.map((l, i) => (
                          <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-electric font-bold text-sm hover:underline" data-testid={`post-${p.id}-link-${i}`}>
                            <LinkIcon className="w-3.5 h-3.5" aria-hidden="true" /> {l.label}
                          </a>
                        ))}
                      </div>
                    )}
                    {isLeader && (
                      <div className="mt-5 pt-4 border-t border-white/5 flex gap-2 justify-end">
                        <button onClick={() => { setEditing(p); setShowCompose(true); }} className="p-2 rounded-lg hover:bg-white/5 text-zinc-400 hover:text-electric" aria-label="Edit post" data-testid={`edit-post-${p.id}`}>
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm("Delete this post?")) return;
                            try {
                              await api.delete(`/portal/groups/${groupId}/posts/${p.id}`);
                              toast.success("Deleted");
                              loadByGroupId(groupId);
                            } catch { toast.error("Delete failed"); }
                          }}
                          className="p-2 rounded-lg hover:bg-red-500/10 text-zinc-400 hover:text-red-400"
                          aria-label="Delete post"
                          data-testid={`delete-post-${p.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </motion.article>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <MembersTab groupId={groupId} onChange={() => loadByGroupId(groupId)} />
      )}
    </div>
  );
}

function PostComposer({ groupId, initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: initial?.title || "",
    body: initial?.body || "",
    category: initial?.category || "announcement",
    links: initial?.links || [],
    event: initial?.event || null,
    poll: initial?.poll || null,
  });
  const [submitting, setSubmitting] = useState(false);
  const inputCls = "w-full px-4 py-3 rounded-lg bg-[#050505] border border-white/10 text-white placeholder-zinc-600 focus:outline-none focus:border-electric focus-visible:ring-2 focus-visible:ring-electric/40 transition";

  const setCategory = (key) => {
    const next = { ...form, category: key };
    // auto-init data blocks based on category
    if (key === "event" && !next.event) next.event = { event_date: "", start_time: "", end_time: "", location: "" };
    if (key === "poll" && !next.poll) next.poll = { question: "", options: ["", ""], closes_at: "" };
    setForm(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        title: form.title,
        body: form.body,
        category: form.category,
        links: (form.links || []).filter((l) => l.url && l.label),
      };
      if (form.category === "event" && form.event?.event_date && form.event?.start_time) {
        payload.event = {
          event_date: form.event.event_date,
          start_time: form.event.start_time,
          end_time: form.event.end_time || null,
          location: form.event.location || null,
        };
      }
      if (form.category === "poll" && form.poll?.question && (form.poll.options || []).filter((o) => o.trim()).length >= 2) {
        payload.poll = {
          question: form.poll.question,
          options: form.poll.options.map((o) => o.trim()).filter(Boolean),
          closes_at: form.poll.closes_at || null,
        };
      }
      if (initial?.id) {
        await api.put(`/portal/groups/${groupId}/posts/${initial.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post(`/portal/groups/${groupId}/posts`, payload);
        toast.success("Posted — emails sent to members");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.form
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      onSubmit={submit}
      className="mb-6 p-7 rounded-2xl bg-[#0a0a0a] border border-electric/20 space-y-4"
      data-testid="post-composer"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-xl">{initial ? "Edit post" : "New post"}</h3>
        <button type="button" onClick={onClose} aria-label="Close" className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-2">Category</label>
        <div className="flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setCategory(c.key)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider border transition ${
                form.category === c.key ? "border-electric bg-electric/10 text-electric" : "border-white/10 text-zinc-400"
              }`}
              data-testid={`composer-cat-${c.key}`}
            >
              <c.icon className="w-3 h-3" /> {c.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5">Title</label>
        <input className={inputCls} required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="composer-title" />
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5">Body (Markdown supported — # headings, **bold**, lists)</label>
        <textarea rows={form.category === "event" || form.category === "poll" ? 4 : 8} className={`${inputCls} font-mono text-sm`} required value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Write your post..." data-testid="composer-body" />
      </div>

      {/* EVENT FIELDS */}
      {form.category === "event" && (
        <div className="p-4 rounded-xl border border-electric/20 bg-electric/5 space-y-3" data-testid="composer-event">
          <div className="text-[10px] uppercase tracking-[0.25em] text-electric font-bold inline-flex items-center gap-2"><Calendar className="w-3 h-3" /> Event details (an .ics will be emailed)</div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] text-zinc-400 font-bold block mb-1.5">Date</label>
              <input type="date" required className={inputCls} value={form.event?.event_date || ""} onChange={(e) => setForm({ ...form, event: { ...form.event, event_date: e.target.value } })} data-testid="composer-event-date" />
            </div>
            <div>
              <label className="text-[10px] text-zinc-400 font-bold block mb-1.5">Start time</label>
              <input type="time" required className={inputCls} value={form.event?.start_time || ""} onChange={(e) => setForm({ ...form, event: { ...form.event, start_time: e.target.value } })} data-testid="composer-event-start" />
            </div>
            <div>
              <label className="text-[10px] text-zinc-400 font-bold block mb-1.5">End time</label>
              <input type="time" className={inputCls} value={form.event?.end_time || ""} onChange={(e) => setForm({ ...form, event: { ...form.event, end_time: e.target.value } })} data-testid="composer-event-end" />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-zinc-400 font-bold block mb-1.5">Location (optional)</label>
            <input className={inputCls} value={form.event?.location || ""} onChange={(e) => setForm({ ...form, event: { ...form.event, location: e.target.value } })} placeholder="Sanctuary, 6217 Portsmouth Blvd..." data-testid="composer-event-location" />
          </div>
        </div>
      )}

      {/* POLL FIELDS */}
      {form.category === "poll" && (
        <div className="p-4 rounded-xl border border-electric/20 bg-electric/5 space-y-3" data-testid="composer-poll">
          <div className="text-[10px] uppercase tracking-[0.25em] text-electric font-bold inline-flex items-center gap-2"><BarChart3 className="w-3 h-3" /> Poll (members vote once each)</div>
          <div>
            <label className="text-[10px] text-zinc-400 font-bold block mb-1.5">Question</label>
            <input required className={inputCls} value={form.poll?.question || ""} onChange={(e) => setForm({ ...form, poll: { ...form.poll, question: e.target.value } })} placeholder="What time should we meet this Saturday?" data-testid="composer-poll-question" />
          </div>
          <div>
            <label className="text-[10px] text-zinc-400 font-bold block mb-1.5">Options (min 2)</label>
            {(form.poll?.options || []).map((opt, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input className={inputCls} value={opt} placeholder={`Option ${i + 1}`} onChange={(e) => {
                  const next = [...form.poll.options]; next[i] = e.target.value;
                  setForm({ ...form, poll: { ...form.poll, options: next } });
                }} data-testid={`composer-poll-option-${i}`} />
                {form.poll.options.length > 2 && (
                  <button type="button" onClick={() => {
                    const next = form.poll.options.filter((_, j) => j !== i);
                    setForm({ ...form, poll: { ...form.poll, options: next } });
                  }} aria-label="Remove option" className="p-2 rounded-lg hover:bg-red-500/10 text-zinc-400 hover:text-red-400">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <button type="button" onClick={() => setForm({ ...form, poll: { ...form.poll, options: [...(form.poll.options || []), ""] } })} className="text-xs text-electric font-bold inline-flex items-center gap-1" data-testid="composer-poll-add">
              <Plus className="w-3 h-3" /> Add option
            </button>
          </div>
          <div>
            <label className="text-[10px] text-zinc-400 font-bold block mb-1.5">Closes at (optional, ISO datetime)</label>
            <input type="datetime-local" className={inputCls} value={form.poll?.closes_at?.slice(0, 16) || ""} onChange={(e) => setForm({ ...form, poll: { ...form.poll, closes_at: e.target.value ? new Date(e.target.value).toISOString() : "" } })} data-testid="composer-poll-closes" />
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold">Links (optional)</label>
          <button type="button" onClick={() => setForm({ ...form, links: [...form.links, { label: "", url: "" }] })} className="text-xs text-electric font-bold inline-flex items-center gap-1" data-testid="composer-add-link">
            <Plus className="w-3 h-3" /> Add link
          </button>
        </div>
        {form.links.map((l, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <input className={inputCls} placeholder="Label (e.g. Spotify playlist)" value={l.label} onChange={(e) => {
              const next = [...form.links]; next[i] = { ...next[i], label: e.target.value }; setForm({ ...form, links: next });
            }} data-testid={`composer-link-label-${i}`} />
            <input className={inputCls} placeholder="https://..." value={l.url} onChange={(e) => {
              const next = [...form.links]; next[i] = { ...next[i], url: e.target.value }; setForm({ ...form, links: next });
            }} data-testid={`composer-link-url-${i}`} />
            <button type="button" onClick={() => setForm({ ...form, links: form.links.filter((_, j) => j !== i) })} aria-label="Remove link" className="p-2 rounded-lg hover:bg-red-500/10 text-zinc-400 hover:text-red-400">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 bg-electric text-black px-6 py-3 rounded-full font-bold text-sm hover:bg-[#4ade80] transition disabled:opacity-60" data-testid="composer-submit">
        <Send className="w-4 h-4" />
        {submitting ? "Posting..." : initial ? "Save changes" : "Post & Notify Group"}
      </button>
    </motion.form>
  );
}

function EventBlock({ post }) {
  const ev = post.event || {};
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/portal/posts/${post.id}/ics`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/calendar" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `push-event-${post.id.slice(0, 8)}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Calendar invite downloaded");
    } catch (err) {
      toast.error("Could not download invite");
    } finally {
      setDownloading(false);
    }
  };

  const fmtDate = () => {
    try {
      const d = new Date(`${ev.event_date}T${ev.start_time || "00:00"}`);
      if (isNaN(d)) return ev.event_date;
      return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    } catch { return ev.event_date; }
  };

  return (
    <div className="mt-5 p-5 rounded-xl border border-electric/20 bg-electric/5 flex flex-wrap items-start justify-between gap-4" data-testid={`post-event-${post.id}`}>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.25em] text-electric font-bold mb-1 inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Event</div>
        <div className="text-white font-bold">{fmtDate()}</div>
        <div className="text-zinc-300 text-sm">
          {ev.start_time}{ev.end_time ? ` – ${ev.end_time}` : ""}
        </div>
        {ev.location && (
          <div className="text-zinc-400 text-sm mt-1 inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {ev.location}</div>
        )}
      </div>
      <button onClick={handleDownload} disabled={downloading} className="inline-flex items-center gap-2 bg-electric text-black px-4 py-2 rounded-full font-bold text-xs hover:bg-[#4ade80] transition disabled:opacity-60" data-testid={`download-ics-${post.id}`}>
        <Download className="w-3.5 h-3.5" /> {downloading ? "..." : "Add to Calendar"}
      </button>
    </div>
  );
}

function PollBlock({ post, groupId, onVoted }) {
  const poll = post.poll || {};
  const options = poll.options || [];
  const counts = poll.vote_counts || options.map(() => 0);
  const total = counts.reduce((a, b) => a + b, 0);
  const myVote = poll.my_vote;
  const closed = poll.closes_at ? (new Date(poll.closes_at) < new Date()) : false;
  const showResults = myVote != null || closed;
  const [voting, setVoting] = useState(null);

  const submit = async (i) => {
    setVoting(i);
    try {
      await api.post(`/portal/groups/${groupId}/posts/${post.id}/vote`, { option_index: i });
      toast.success("Vote recorded");
      onVoted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Vote failed");
    } finally { setVoting(null); }
  };

  return (
    <div className="mt-5 p-5 rounded-xl border border-electric/20 bg-electric/5" data-testid={`post-poll-${post.id}`}>
      <div className="text-[10px] uppercase tracking-[0.25em] text-electric font-bold mb-2 inline-flex items-center gap-1.5"><BarChart3 className="w-3 h-3" /> Poll{closed ? " · Closed" : ""}</div>
      <div className="text-white font-bold mb-4">{poll.question}</div>
      <div className="space-y-2">
        {options.map((o, i) => {
          const c = counts[i] || 0;
          const pct = total > 0 ? Math.round((c / total) * 100) : 0;
          const mine = myVote === i;
          if (showResults) {
            return (
              <div key={i} className={`relative rounded-lg overflow-hidden border ${mine ? "border-electric" : "border-white/10"} bg-[#050505]`} data-testid={`poll-result-${post.id}-${i}`}>
                <div className="absolute inset-y-0 left-0 bg-electric/15" style={{ width: `${pct}%` }} aria-hidden="true" />
                <div className="relative flex items-center justify-between px-4 py-2.5 text-sm">
                  <span className="text-white inline-flex items-center gap-2">{mine && <Check className="w-3.5 h-3.5 text-electric" />} {o}</span>
                  <span className="text-electric font-bold">{pct}% · {c}</span>
                </div>
              </div>
            );
          }
          return (
            <button
              key={i}
              onClick={() => submit(i)}
              disabled={voting !== null || closed}
              className="w-full text-left px-4 py-2.5 rounded-lg border border-white/10 bg-[#050505] hover:border-electric hover:bg-electric/5 transition text-sm disabled:opacity-60"
              data-testid={`poll-vote-${post.id}-${i}`}
            >
              {voting === i ? "Voting..." : o}
            </button>
          );
        })}
      </div>
      <div className="text-[10px] text-zinc-500 mt-3 uppercase tracking-wider">{total} {total === 1 ? "vote" : "votes"}{poll.closes_at && !closed ? ` · Closes ${new Date(poll.closes_at).toLocaleString()}` : ""}</div>
    </div>
  );
}

function MembersTab({ groupId, onChange }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    try {
      const { data } = await api.get(`/portal/groups/${groupId}/memberships`);
      setList(data);
    } catch (e) { toast.error("Failed to load members"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [groupId]);

  const decide = async (m, status) => {
    try {
      await api.put(`/portal/groups/${groupId}/memberships/${m.id}`, { status });
      toast.success(`${status === "approved" ? "Approved" : "Declined"}`);
      load();
      onChange?.();
    } catch (e) { toast.error("Failed"); }
  };

  const filtered = statusFilter === "all" ? list : list.filter((m) => m.status === statusFilter);
  const pendingCount = list.filter((m) => m.status === "pending").length;

  if (loading) return <div className="py-10 flex justify-center"><div className="w-8 h-8 border-2 border-electric border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="members-tab">
      <div className="flex gap-2 mb-5">
        {[
          { key: "all", label: "All" },
          { key: "pending", label: `Pending${pendingCount ? ` (${pendingCount})` : ""}` },
          { key: "approved", label: "Approved" },
          { key: "declined", label: "Declined" },
        ].map((f) => (
          <button key={f.key} onClick={() => setStatusFilter(f.key)} className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition ${statusFilter === f.key ? "bg-electric text-black" : "bg-white/5 text-zinc-400 hover:text-white"}`} data-testid={`members-filter-${f.key}`}>
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="py-12 text-center text-zinc-500 text-sm">No members in this view.</div>
      ) : (
        <div className="space-y-2">
          {filtered.map((m) => (
            <div key={m.id} className="p-4 rounded-xl bg-[#0a0a0a] border border-white/5 flex items-center justify-between gap-4" data-testid={`member-row-${m.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-bold">{m.member_name}</div>
                <div className="text-sm text-zinc-400">{m.member_email}</div>
              </div>
              <div className="flex items-center gap-2">
                {m.status === "pending" && (
                  <>
                    <button onClick={() => decide(m, "approved")} aria-label="Approve" className="p-2 rounded-lg bg-electric/10 text-electric hover:bg-electric/20" data-testid={`approve-${m.id}`}><Check className="w-4 h-4" /></button>
                    <button onClick={() => decide(m, "declined")} aria-label="Decline" className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20" data-testid={`decline-${m.id}`}><X className="w-4 h-4" /></button>
                  </>
                )}
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border font-bold ${
                  m.status === "approved" ? "bg-electric/10 text-electric border-electric/20" :
                  m.status === "pending" ? "bg-yellow-500/10 text-yellow-400 border-yellow-500/20" :
                  "bg-red-500/10 text-red-400 border-red-500/20"
                }`}>
                  {m.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
